import { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { makePrioStyles } from '../../../theme/utils';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import Flex from '../../../components/Flex';
import { Input, TextArea } from '@prio365/prio365-react-library';
import { DashboardItem } from '../../../models/Dashboard';
import { PowerBIData } from './DashboardItems/DashboardPowerBIItem';
import { parsePowerBIData } from './util';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  previewHeader: {
    fontSize: theme.font.fontSize.small,
    fontWeight: theme.font.fontWeight.bold,
    color: theme.colors.application.typography.default,
    borderBottom: `2px solid ${theme.colors.application.typography.default}`,
    width: 'fit-content',
    marginTop: theme.spacing.regular,
  },
}));

export interface DashboardEditWidgetPowerBIContentDrawerHandle {
  getData: () => { title: string; link: string };
}

interface DashboardEditWidgetPowerBIContentDrawerProps {
  className?: string;
  dashboardItem: DashboardItem;
  onValidityChange?: (isValid: boolean) => void;
}

export const DashboardEditWidgetPowerBIContentDrawer = forwardRef<
  DashboardEditWidgetPowerBIContentDrawerHandle,
  DashboardEditWidgetPowerBIContentDrawerProps
>((props, ref) => {
  //#region ------------------------------ Defaults
  const { className, dashboardItem, onValidityChange } = props;
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const powerBIData: PowerBIData = parsePowerBIData<PowerBIData>(
    dashboardItem?.data
  );

  const [title, setTitle] = useState<string>(
    powerBIData?.title || t('dashboard:powerBI.defaultTitle')
  );
  const [link, setLink] = useState<string>(powerBIData?.link || '');
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const getNormalizedLink = (l: string) => {
    const trimmed = l.trim();
    if (!trimmed) return '';
    return trimmed.startsWith('https://') ? trimmed : `https://${trimmed}`;
  };
  //#endregion

  //#region ------------------------------ Effects

  useEffect(() => {
    if (onValidityChange) {
      const isValid = !!title.trim() && !!link.trim();
      onValidityChange(isValid);
    }
  }, [title, link, onValidityChange]);

  useImperativeHandle(
    ref,
    () => ({
      getData: () => ({ title, link: getNormalizedLink(link) }),
    }),
    [title, link]
  );

  //#endregion

  //#region ------------------------------ Component
  //#endregion

  return (
    <Flex.Column
      className={classNames(className, classes.root)}
      childrenGap={theme.spacing.regular}
    >
      <Input
        label={`${t('dashboard:powerBI.title')} *`}
        value={title}
        onChangeWithoutDebounce={setTitle}
        full
        required
      />
      <TextArea
        label={`${t('dashboard:powerBI.link')} *`}
        value={link}
        onChange={setLink}
        required
      />
      {link && (
        <>
          <div className={classes.previewHeader}>
            {t('dashboard:powerBI.preview')}
          </div>
          <iframe
            src={getNormalizedLink(link)}
            title={title}
            width="100%"
            height="100%"
            style={{ border: 0, minHeight: 400 }}
            allowFullScreen
          />
        </>
      )}
    </Flex.Column>
  );
});

export default DashboardEditWidgetPowerBIContentDrawer;
